(() => {
  'use strict';

  const JOURNEY_SELECTOR = '[data-deeb-signature-journey]';
  const STEP_SELECTOR = '[data-deeb-signature-journey-step]';
  const CAROUSEL_SELECTOR = '[data-deeb-signature-carousel]';
  const TRACK_SELECTOR = '[data-deeb-signature-carousel-track]';
  const PREV_SELECTOR = '[data-deeb-signature-carousel-prev]';
  const NEXT_SELECTOR = '[data-deeb-signature-carousel-next]';
  const ENHANCED_CLASS = 'is-enhanced';
  const SEEN_CLASS = 'is-seen';

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );
  const instances = new WeakMap();

  class DeebSignatureJourney {
    constructor(root) {
      this.root = root;
      this.steps = Array.from(root.querySelectorAll(STEP_SELECTOR));
      this.observer = null;
      this.track = null;
      this.prevButton = null;
      this.nextButton = null;
      this.onPrev = null;
      this.onNext = null;
      this.onTrackScroll = null;

      this.init();
    }

    init() {
      this.root.dataset.deebJourneyReady = 'true';
      this.initJourneyReveal();
      this.initCarousel();
    }

    initJourneyReveal() {
      if (
        prefersReducedMotion.matches ||
        !('IntersectionObserver' in window) ||
        this.steps.length === 0
      ) {
        this.steps.forEach((step) => step.classList.add(SEEN_CLASS));
        return;
      }

      this.root.classList.add(ENHANCED_CLASS);

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add(SEEN_CLASS);
            this.observer?.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -10% 0px',
          threshold: 0.24,
        }
      );

      this.steps.forEach((step) => this.observer.observe(step));
    }

    initCarousel() {
      if (!this.root.matches(CAROUSEL_SELECTOR)) return;

      this.track = this.root.querySelector(TRACK_SELECTOR);
      this.prevButton = this.root.querySelector(PREV_SELECTOR);
      this.nextButton = this.root.querySelector(NEXT_SELECTOR);

      if (!this.track || !this.prevButton || !this.nextButton) return;

      this.onPrev = () => this.moveCarousel(-1);
      this.onNext = () => this.moveCarousel(1);
      this.onTrackScroll = () => this.updateCarouselButtons();

      this.prevButton.addEventListener('click', this.onPrev);
      this.nextButton.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });

      this.updateCarouselButtons();
    }

    getCarouselStep() {
      const firstStep = this.track?.querySelector(STEP_SELECTOR);
      if (!firstStep) return this.track?.clientWidth || 0;

      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;

      return firstStep.getBoundingClientRect().width + gap;
    }

    moveCarousel(direction) {
      if (!this.track) return;

      const distance = this.getCarouselStep() * direction;
      this.track.scrollBy({
        left: distance,
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    updateCarouselButtons() {
      if (!this.track || !this.prevButton || !this.nextButton) return;

      const epsilon = 4;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const current = this.track.scrollLeft;

      this.prevButton.disabled = current <= epsilon;
      this.nextButton.disabled = current >= maxScroll - epsilon;
    }

    destroy() {
      this.observer?.disconnect();
      this.observer = null;

      if (this.prevButton && this.onPrev) {
        this.prevButton.removeEventListener('click', this.onPrev);
      }
      if (this.nextButton && this.onNext) {
        this.nextButton.removeEventListener('click', this.onNext);
      }
      if (this.track && this.onTrackScroll) {
        this.track.removeEventListener('scroll', this.onTrackScroll);
      }

      delete this.root.dataset.deebJourneyReady;
      this.root.classList.remove(ENHANCED_CLASS);
      instances.delete(this.root);
    }
  }

  const mountJourneys = (scope = document) => {
    const roots = [];

    if (scope.matches?.(JOURNEY_SELECTOR)) roots.push(scope);

    const nestedRoots = scope.querySelectorAll?.(JOURNEY_SELECTOR) || [];
    roots.push(...nestedRoots);

    roots.forEach((root) => {
      if (instances.has(root)) return;

      const instance = new DeebSignatureJourney(root);
      instances.set(root, instance);
    });
  };

  const destroyJourneys = (scope) => {
    const roots = [];

    if (scope.matches?.(JOURNEY_SELECTOR)) roots.push(scope);

    const nestedRoots = scope.querySelectorAll?.(JOURNEY_SELECTOR) || [];
    roots.push(...nestedRoots);

    roots.forEach((root) => instances.get(root)?.destroy());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountJourneys(), {
      once: true,
    });
  } else {
    mountJourneys();
  }

  document.addEventListener('shopify:section:load', (event) => {
    mountJourneys(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyJourneys(event.target);
  });
})();
